import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Typography,
  Box,
  Grid,
  Paper,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  IconButton,
  Button,
  Tabs,
  Tab,
  Tooltip,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as SessionIcon,
  Dashboard as LiveIcon,
  Edit as EditIcon,
  ContentCopy as ContentCopyIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { workshopService } from '../../services/workshop.service';
import { sessionService } from '../../services/session.service';
import { Workshop, DashboardStats, WorkshopCategory, WorkshopDifficulty } from '../../types/workshop.types';
import { Session } from '../../types/session.types';
import { useNotification } from '../../context/NotificationContext';

const categoryLabels: Record<WorkshopCategory, string> = {
  privilege_cloud: 'Privilege Cloud',
  identity_security: 'Identity Security',
  pam_self_hosted: 'PAM Self-Hosted',
  secrets_manager: 'Secrets Manager',
  endpoint_privilege_manager: 'Endpoint Privilege Manager',
  other: 'Other',
};

const difficultyConfig: Record<WorkshopDifficulty, { label: string; color: 'success' | 'info' | 'warning' | 'error' }> = {
  beginner: { label: 'Beginner', color: 'success' },
  intermediate: { label: 'Intermediate', color: 'info' },
  advanced: { label: 'Advanced', color: 'warning' },
  expert: { label: 'Expert', color: 'error' },
};

const statusConfig: Record<string, { label: string; color: 'default' | 'success' | 'warning' }> = {
  draft: { label: 'Draft', color: 'default' },
  published: { label: 'Published', color: 'success' },
  archived: { label: 'Archived', color: 'warning' },
};

type SortField = 'title' | 'updatedAt';
type SortOrder = 'asc' | 'desc';

const InstructorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const showNotificationRef = useRef(showNotification);
  showNotificationRef.current = showNotification;

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [sortField, setSortField] = useState<SortField>('updatedAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Shared Library state
  const [libraryWorkshops, setLibraryWorkshops] = useState<Workshop[]>([]);
  const [libraryTotal, setLibraryTotal] = useState(0);
  const [libraryPage, setLibraryPage] = useState(0);
  const [libraryRowsPerPage, setLibraryRowsPerPage] = useState(10);
  const [librarySortField, setLibrarySortField] = useState<string>('updatedAt');
  const [librarySortOrder, setLibrarySortOrder] = useState<string>('desc');
  const [libraryCategory, setLibraryCategory] = useState<string>('');
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneTarget, setCloneTarget] = useState<Workshop | null>(null);
  const [cloning, setCloning] = useState(false);

  // Invited sessions (co-instructed)
  const [invitedSessions, setInvitedSessions] = useState<Session[]>([]);

  // FM8: Fetch stats only on mount, not on pagination changes
  useEffect(() => {
    const loadStats = async () => {
      try {
        const statsData = await workshopService.getDashboardStats();
        setStats(statsData);
      } catch {
        showNotificationRef.current('Failed to load dashboard stats', 'error');
      }
    };
    loadStats();

    // Load invited sessions (co-instructed)
    sessionService.getSessions()
      .then((all) => setInvitedSessions(all.filter((s) => s.role === 'co-instructor')))
      .catch(() => {});
  }, []);

  // Fetch workshops on pagination changes
  const loadWorkshops = useCallback(async () => {
    setLoading(true);
    try {
      const workshopData = await workshopService.getWorkshops({
        page: page + 1,
        limit: rowsPerPage,
        sortBy: sortField,
        sortOrder: sortOrder.toUpperCase(),
      });
      setWorkshops(workshopData.data);
      setTotal(workshopData.total);
    } catch {
      showNotificationRef.current('Failed to load tech evals', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, sortField, sortOrder]);

  useEffect(() => {
    loadWorkshops();
  }, [loadWorkshops]);

  // Fetch shared library when tab is active
  useEffect(() => {
    if (tabValue !== 1) return;
    const fetchLibrary = async () => {
      setLibraryLoading(true);
      try {
        const data = await workshopService.getLibrary({
          page: libraryPage + 1,
          limit: libraryRowsPerPage,
          sortBy: librarySortField,
          sortOrder: librarySortOrder.toUpperCase(),
          ...(libraryCategory && { category: libraryCategory }),
        });
        setLibraryWorkshops(data.data);
        setLibraryTotal(data.total);
      } catch (err) {
        showNotificationRef.current('Failed to load shared library', 'error');
      } finally {
        setLibraryLoading(false);
      }
    };
    fetchLibrary();
  }, [tabValue, libraryPage, libraryRowsPerPage, librarySortField, librarySortOrder, libraryCategory]);

  const handleClone = async () => {
    if (!cloneTarget) return;
    setCloning(true);
    try {
      await workshopService.cloneWorkshop(cloneTarget.id);
      await loadWorkshops();
      showNotificationRef.current('Tech Eval cloned successfully! Check "My Tech Evals" for the draft copy.', 'success');
      setCloneDialogOpen(false);
      setCloneTarget(null);
      setTabValue(0); // Switch to My Tech Evals tab
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to clone tech eval';
      showNotificationRef.current(msg, 'error');
    } finally {
      setCloning(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Archive/Unarchive/Delete handlers
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const handleArchive = async (workshopId: string) => {
    try {
      await workshopService.updateWorkshop(workshopId, { status: 'archived' });
      showNotificationRef.current('Tech Eval archived', 'success');
      loadWorkshops();
    } catch (err: any) {
      showNotificationRef.current(err?.response?.data?.message || 'Failed to archive', 'error');
    }
  };

  const handleUnarchive = async (workshopId: string) => {
    if (!window.confirm('This will return the tech eval to Draft status. You will need to publish it again to make it available for sessions. Continue?')) return;
    try {
      await workshopService.updateWorkshop(workshopId, { status: 'draft' });
      showNotificationRef.current('Tech Eval unarchived. It is now in Draft status.', 'success');
      loadWorkshops();
    } catch (err: any) {
      showNotificationRef.current(err?.response?.data?.message || 'Failed to unarchive', 'error');
    }
  };

  const handleDeleteClick = (workshopId: string) => {
    setDeleteTargetId(workshopId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    try {
      await workshopService.deleteWorkshop(deleteTargetId);
      showNotificationRef.current('Tech Eval deleted', 'success');
      setDeleteDialogOpen(false);
      setDeleteTargetId(null);
      loadWorkshops();
    } catch (err: any) {
      showNotificationRef.current(err?.response?.data?.message || 'Failed to delete', 'error');
    }
  };

  const statCards = [
    { label: 'Total Tech Evals', value: stats?.totalWorkshops ?? 0 },
    { label: 'Total Sessions', value: stats?.totalSessions ?? 0 },
    { label: 'Total Students', value: stats?.totalStudents ?? 0 },
    { label: 'Completion Rate', value: `${stats?.avgCompletionRate ?? 0}%` },
  ];

  return (
    <Box>
      {/* Header with action buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Instructor Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/instructor/workshop/new')}
          >
            Create Tech Eval
          </Button>
          <Button
            variant="outlined"
            startIcon={<SessionIcon />}
            onClick={() => total > 0 ? navigate('/instructor/sessions/new') : undefined}
            disabled={total === 0}
          >
            New Session
          </Button>
        </Box>
      </Box>

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((card) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={card.label}>
            {loading ? (
              <Paper elevation={1} sx={{ p: '20px', textAlign: 'center' }}>
                <Skeleton variant="text" width="60%" sx={{ mx: 'auto', fontSize: '2rem' }} />
                <Skeleton variant="text" width="80%" sx={{ mx: 'auto' }} />
              </Paper>
            ) : (
              <Paper elevation={1} sx={{ p: '20px', textAlign: 'center' }}>
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {card.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.label}
                </Typography>
              </Paper>
            )}
          </Grid>
        ))}
      </Grid>

      {/* Invited Sessions */}
      {invitedSessions.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Invited Sessions
          </Typography>
          <Grid container spacing={2}>
            {invitedSessions.map((s) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={s.id}>
                <Card
                  sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 }, height: '100%' }}
                  onClick={() => navigate(`/instructor/session/${s.id}`)}
                >
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} noWrap>
                      {s.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {s.workshop?.title || '—'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <Chip
                        label={s.status}
                        size="small"
                        color={s.status === 'active' ? 'success' : s.status === 'scheduled' ? 'info' : s.status === 'preparing' || s.status === 'ready' ? 'warning' : 'default'}
                      />
                      {s.scheduledStart && (
                        <Typography variant="caption" color="text.secondary">
                          {dayjs(s.scheduledStart).format('MMM D, YYYY')}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Tabs */}
      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 3 }}>
        <Tab label="My Tech Evals" />
        <Tab label="Shared Library" />
        <Tab label="Live Sessions" disabled />
        <Tab label="Analytics" disabled />
      </Tabs>

      {/* Workshops Table */}
      {tabValue === 0 && (
        <>
          <TableContainer component={Paper} elevation={1}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'title'}
                      direction={sortField === 'title' ? sortOrder : 'asc'}
                      onClick={() => handleSort('title')}
                    >
                      Tech Eval Title
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Difficulty</TableCell>
                  <TableCell>Steps</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'updatedAt'}
                      direction={sortField === 'updatedAt' ? sortOrder : 'asc'}
                      onClick={() => handleSort('updatedAt')}
                    >
                      Last Updated
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 7 }).map((__, j) => (
                        <TableCell key={j}>
                          <Skeleton variant="text" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : workshops.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No tech evals yet</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  workshops.map((workshop) => (
                    <TableRow key={workshop.id} hover>
                      <TableCell>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            '&:hover': { color: 'primary.main' },
                          }}
                          onClick={() => navigate(`/instructor/workshop/${workshop.id}/edit`)}
                        >
                          {workshop.title}
                        </Typography>
                      </TableCell>
                      <TableCell>{categoryLabels[workshop.category] || workshop.category}</TableCell>
                      <TableCell>
                        <Chip
                          label={difficultyConfig[workshop.difficulty]?.label || workshop.difficulty}
                          color={difficultyConfig[workshop.difficulty]?.color || 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{workshop.stepCount ?? 0}</TableCell>
                      <TableCell>
                        <Chip
                          label={statusConfig[workshop.status]?.label || workshop.status}
                          color={statusConfig[workshop.status]?.color || 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {dayjs(workshop.updatedAt).fromNow()}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.25 }}>
                          <Tooltip title={workshop.status === 'archived' ? 'View (read-only)' : 'Edit'}>
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/instructor/workshop/${workshop.id}/edit`)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {workshop.status === 'published' && (
                            <Tooltip title="Archive">
                              <IconButton
                                size="small"
                                onClick={() => handleArchive(workshop.id)}
                              >
                                <ArchiveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {workshop.status === 'archived' && (
                            <Tooltip title="Unarchive (return to Draft)">
                              <IconButton
                                size="small"
                                onClick={() => handleUnarchive(workshop.id)}
                              >
                                <UnarchiveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {(workshop.status === 'draft' || workshop.status === 'archived') && (
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteClick(workshop.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {!loading && total > 0 && (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          )}
        </>
      )}

      {/* Shared Library Tab */}
      {tabValue === 1 && (
        <Box>
          {/* Category filter */}
          <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={libraryCategory}
                label="Category"
                onChange={(e) => { setLibraryCategory(e.target.value); setLibraryPage(0); }}
              >
                <MenuItem value="">All Categories</MenuItem>
                <MenuItem value="privilege_cloud">Privilege Cloud</MenuItem>
                <MenuItem value="identity_security">Identity Security</MenuItem>
                <MenuItem value="pam_self_hosted">PAM Self-Hosted</MenuItem>
                <MenuItem value="secrets_manager">Secrets Manager</MenuItem>
                <MenuItem value="endpoint_privilege_manager">EPM</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={librarySortField === 'title'}
                      direction={librarySortField === 'title' ? (librarySortOrder as 'asc' | 'desc') : 'asc'}
                      onClick={() => {
                        if (librarySortField === 'title') {
                          setLibrarySortOrder(librarySortOrder === 'asc' ? 'desc' : 'asc');
                        } else {
                          setLibrarySortField('title');
                          setLibrarySortOrder('asc');
                        }
                      }}
                    >
                      Title
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Difficulty</TableCell>
                  <TableCell align="center">Steps</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={librarySortField === 'updatedAt'}
                      direction={librarySortField === 'updatedAt' ? (librarySortOrder as 'asc' | 'desc') : 'desc'}
                      onClick={() => {
                        if (librarySortField === 'updatedAt') {
                          setLibrarySortOrder(librarySortOrder === 'asc' ? 'desc' : 'asc');
                        } else {
                          setLibrarySortField('updatedAt');
                          setLibrarySortOrder('desc');
                        }
                      }}
                    >
                      Last Updated
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {libraryLoading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 7 }).map((_, j) => (
                        <TableCell key={j}><Skeleton /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : libraryWorkshops.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No shared tech evals yet. Instructors can share published tech evals to the library.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  libraryWorkshops.map((ws) => (
                    <TableRow key={ws.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {ws.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {ws.instructor ? `${ws.instructor.firstName} ${ws.instructor.lastName}` : '\u2014'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={ws.category?.replace(/_/g, ' ') || 'Other'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip label={ws.difficulty} size="small" />
                      </TableCell>
                      <TableCell align="center">
                        {(ws as any).stepCount ?? '\u2014'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {dayjs(ws.updatedAt).format('MMM D, YYYY')}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Clone to your tech evals">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => { setCloneTarget(ws); setCloneDialogOpen(true); }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={libraryTotal}
            page={libraryPage}
            onPageChange={(_, p) => setLibraryPage(p)}
            rowsPerPage={libraryRowsPerPage}
            onRowsPerPageChange={(e) => { setLibraryRowsPerPage(parseInt(e.target.value, 10)); setLibraryPage(0); }}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </Box>
      )}

      {/* Clone Confirmation Dialog */}
      <Dialog open={cloneDialogOpen} onClose={() => setCloneDialogOpen(false)}>
        <DialogTitle>Clone Tech Eval</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Clone &quot;{cloneTarget?.title}&quot; to your tech evals? You&apos;ll get an editable draft copy.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneDialogOpen(false)} disabled={cloning}>Cancel</Button>
          <Button onClick={handleClone} variant="contained" disabled={cloning}>
            {cloning ? 'Cloning...' : 'Clone'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Tech Eval</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete this tech eval and all its steps. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Quick Actions */}
      <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
        Quick Actions
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              textAlign: 'center',
              border: '2px dashed',
              borderColor: 'divider',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'primary.50',
              },
            }}
            onClick={() => navigate('/instructor/workshop/new')}
          >
            <AddIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Create Tech Eval
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Design a new tech eval experience
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Tooltip title={total > 0 ? '' : 'Create a tech eval first'} arrow>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                textAlign: 'center',
                border: '2px dashed',
                borderColor: 'divider',
                opacity: total > 0 ? 1 : 0.5,
                cursor: total > 0 ? 'pointer' : 'default',
                transition: 'all 0.2s',
                ...(total > 0 && {
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'primary.50',
                  },
                }),
              }}
              onClick={() => total > 0 && navigate('/instructor/sessions/new')}
            >
              <SessionIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                New Session
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Schedule a tech eval session
              </Typography>
            </Paper>
          </Tooltip>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Tooltip title="Start a session first" arrow>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                textAlign: 'center',
                border: '2px dashed',
                borderColor: 'divider',
                opacity: 0.5,
                cursor: 'default',
              }}
            >
              <LiveIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Live Dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Monitor active sessions in real-time
              </Typography>
            </Paper>
          </Tooltip>
        </Grid>
      </Grid>
    </Box>
  );
};

export default InstructorDashboard;
